/**
 * Market API Service
 * Handles market data fetching (odds, matches by date, in-play, etc.)
 */

import { API_URL } from '@/utils/constants';

/**
 * Fetch market odds for a specific match
 * @param {string} matchId - Market ID
 * @returns {Promise<Object|null>} Formatted odds data or null
 */
export const fetchMarketOdds = async (matchId, timeout = 3000) => {
  try {
    // Add timeout to prevent hanging
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), timeout);
    
    const res = await fetch(
      `${API_URL}/sports/market-odds?market_id=${matchId}`,
      {
        headers: { 'Content-Type': 'application/json' },
        signal: controller.signal,
      }
    );
    
    clearTimeout(timeoutId);
    
    if (!res.ok) return null;
    
    const data = await res.json();
    if (!Array.isArray(data) || data.length === 0) return null;
    
    const [marketOdds] = data;
    if (!marketOdds?.runners) return null;

    const runners = marketOdds.runners;
    const formatted = {
      one: { back: { price: '--', size: 0 }, lay: { price: '--', size: 0 } },
      x: { back: { price: '--', size: 0 }, lay: { price: '--', size: 0 } },
      two: { back: { price: '--', size: 0 }, lay: { price: '--', size: 0 } },
      totalMatched: marketOdds.totalMatched || 0,
    };

    if (runners.length === 2) {
      formatted.one = {
        back: {
          price: runners[0].ex.availableToBack[0]?.price || '--',
          size: runners[0].ex.availableToBack[0]?.size || 0,
        },
        lay: {
          price: runners[0].ex.availableToLay[0]?.price || '--',
          size: runners[0].ex.availableToLay[0]?.size || 0,
        },
      };
      formatted.two = {
        back: {
          price: runners[1].ex.availableToBack[0]?.price || '--',
          size: runners[1].ex.availableToBack[0]?.size || 0,
        },
        lay: {
          price: runners[1].ex.availableToLay[0]?.price || '--',
          size: runners[1].ex.availableToLay[0]?.size || 0,
        },
      };
    } else if (runners.length === 3) {
      formatted.one = {
        back: {
          price: runners[0].ex.availableToBack[0]?.price || '--',
          size: runners[0].ex.availableToBack[0]?.size || 0,
        },
        lay: {
          price: runners[0].ex.availableToLay[0]?.price || '--',
          size: runners[0].ex.availableToLay[0]?.size || 0,
        },
      };
      formatted.x = {
        back: {
          price: runners[1].ex.availableToBack[0]?.price || '--',
          size: runners[1].ex.availableToBack[0]?.size || 0,
        },
        lay: {
          price: runners[1].ex.availableToLay[0]?.price || '--',
          size: runners[1].ex.availableToLay[0]?.size || 0,
        },
      };
      formatted.two = {
        back: {
          price: runners[2].ex.availableToBack[0]?.price || '--',
          size: runners[2].ex.availableToBack[0]?.size || 0,
        },
        lay: {
          price: runners[2].ex.availableToLay[0]?.price || '--',
          size: runners[2].ex.availableToLay[0]?.size || 0,
        },
      };
    }

    return formatted;
  } catch (err) {
    // Don't log timeout errors as errors (they're expected for slow endpoints)
    if (err.name === 'AbortError') {
      console.warn(`Odds fetch timeout for match ${matchId}`);
    } else {
      console.error('Error fetching market odds:', err);
    }
    return null;
  }
};

/**
 * Fetch all in-play matches
 * @returns {Promise<Array>} Array of in-play matches
 */
export const fetchAllInPlay = async () => {
  try {
    const res = await fetch(`${API_URL}/sports/allinplay`, {
      headers: { 'Content-Type': 'application/json' },
    });
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    return await res.json();
  } catch (err) {
    console.error('Error fetching all in-play:', err);
    throw err;
  }
};

/**
 * Fetch all matches
 * @returns {Promise<Array>} Array of all matches
 */
export const fetchAllMatches = async () => {
  try {
    const res = await fetch(`${API_URL}/sports/all-matches`, {
      headers: { 'Content-Type': 'application/json' },
    });
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    return await res.json();
  } catch (err) {
    console.error('Error fetching all matches:', err);
    throw err;
  }
};

/**
 * Fetch in-play matches for a specific sport
 * @param {string} sportId - Sport ID
 * @returns {Promise<Array>} Array of in-play matches for the sport
 */
export const fetchInPlayBySportId = async (sportId) => {
  try {
    const res = await fetch(`${API_URL}/sports/inplayGameId/${sportId}`, {
      headers: { 'Content-Type': 'application/json' },
    });
    if (!res.ok) {
      // Return empty array on HTTP error to show empty state
      return [];
    }
    const data = await res.json();
    // Check if response contains an error message (e.g., "No matches found for the specified game ID")
    if (data?.error || (typeof data === 'object' && !Array.isArray(data) && !data.games)) {
      // Return empty array instead of throwing - this allows empty state to show
      return [];
    }
    return data;
  } catch (err) {
    console.error('Error fetching in-play by sport ID:', err);
    // Return empty array on error to show empty state
    return [];
  }
};

/**
 * Fetch matches for a specific sport
 * @param {string} sportId - Sport ID
 * @returns {Promise<Array>} Array of matches for the sport
 */
export const fetchMatchesBySportId = async (sportId) => {
  try {
    const res = await fetch(`${API_URL}/sports/matches/${sportId}`, {
      headers: { 'Content-Type': 'application/json' },
    });
    if (!res.ok) {
      // Return empty array on HTTP error to show empty state
      return [];
    }
    const data = await res.json();
    // Check if response contains an error message (e.g., "No matches found for the specified game ID")
    if (data?.error || (typeof data === 'object' && !Array.isArray(data) && !data.games)) {
      // Return empty array instead of throwing - this allows empty state to show
      return [];
    }
    return data;
  } catch (err) {
    console.error('Error fetching matches by sport ID:', err);
    // Return empty array on error to show empty state
    return [];
  }
};

/**
 * Fetch matches by date (today)
 * @returns {Promise<Array>} Array of today's matches
 */
export const fetchMatchesToday = async () => {
  try {
    const res = await fetch(`${API_URL}/sports/matches-by-date/today`, {
      headers: { 'Content-Type': 'application/json' },
    });
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    return await res.json();
  } catch (err) {
    console.error('Error fetching matches today:', err);
    throw err;
  }
};

/**
 * Fetch matches by date (tomorrow)
 * @returns {Promise<Array>} Array of tomorrow's matches
 */
export const fetchMatchesTomorrow = async () => {
  try {
    const res = await fetch(`${API_URL}/sports/matches-by-date/tomorrow`, {
      headers: { 'Content-Type': 'application/json' },
    });
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    return await res.json();
  } catch (err) {
    console.error('Error fetching matches tomorrow:', err);
    throw err;
  }
};

/**
 * Fetch matches for a specific sport by date (today)
 * @param {string} sportId - Sport ID
 * @returns {Promise<Array>} Array of today's matches for the sport
 */
export const fetchMatchesTodayBySportId = async (sportId) => {
  try {
    const res = await fetch(`${API_URL}/sports/matches/today/${sportId}`, {
      headers: { 'Content-Type': 'application/json' },
    });
    if (!res.ok) {
      return [];
    }
    const data = await res.json();
    // Check if response contains an error message
    if (data?.error || (typeof data === 'object' && !Array.isArray(data) && !data.games)) {
      return [];
    }
    return data;
  } catch (err) {
    console.error('Error fetching matches today by sport ID:', err);
    return [];
  }
};

/**
 * Fetch matches for a specific sport by date (tomorrow)
 * @param {string} sportId - Sport ID
 * @returns {Promise<Array>} Array of tomorrow's matches for the sport
 */
export const fetchMatchesTomorrowBySportId = async (sportId) => {
  try {
    const res = await fetch(`${API_URL}/sports/matches/tomorrow/${sportId}`, {
      headers: { 'Content-Type': 'application/json' },
    });
    if (!res.ok) {
      return [];
    }
    const data = await res.json();
    // Check if response contains an error message
    if (data?.error || (typeof data === 'object' && !Array.isArray(data) && !data.games)) {
      return [];
    }
    return data;
  } catch (err) {
    console.error('Error fetching matches tomorrow by sport ID:', err);
    return [];
  }
};

/**
 * Sport name mapping
 */
export const SPORT_NAME_MAP = {
  '1': 'Soccer',
  '2': 'Tennis',
  '3': 'Golf',
  '4': 'Cricket',
  '5': 'Rugby Union',
  '6': 'Boxing',
  '7': 'Racing',
  '7522': 'Basketball',
  '6423': 'American Football',
  '7511': 'Baseball',
  '1477': 'Rugby League',
  '3503': 'Darts',
  '6422': 'Snooker',
  '7512': 'Ice Hockey',
  '26420387': 'MMA',
  '4339': 'Greyhound Racing',
  '8': 'Motor Sport',
  '2152880': 'Gaelic Games',
};

/**
 * Collect markets from sports array
 * @param {Array} arr - Array of sports with markets
 * @returns {Array} Flattened array of markets
 */
export const collectMarkets = (arr) => {
  if (!Array.isArray(arr)) return [];
  return arr.flatMap((sport) => {
    const markets = sport?.markets || [];
    return markets.map((m) => ({
      ...m,
      sportId: sport.id || sport.sportId || sport.gameId,
      sportName: sport.name || sport.sportName || sport.gameName || 'Unknown',
      // Preserve marketId for odds fetching
      marketId: m.marketId || m.id,
    }));
  });
};

/**
 * Transform raw market data to match format
 * @param {Object} m - Raw market data
 * @param {string} upperTab - Current tab (in-play, today, tomorrow)
 * @param {string} sportName - Sport name
 * @returns {Object} Formatted match object
 */
export const transformMarketToMatch = (m, upperTab, sportName) => {
  const utcDate = new Date(m.marketStartTime);
  const timeStr = new Intl.DateTimeFormat('en-IN', {
    hour: '2-digit',
    minute: '2-digit',
    hour12: true,
  }).format(utcDate);

  // Check if runners have odds data in the response
  const hasOddsInRunners = m.runners?.some(r => r.ex?.availableToBack || r.ex?.availableToLay || r.lastPriceTraded);
  
  // Try to extract odds from runners if available
  let initialOdds = {
    one: { back: { price: '--', size: 0 }, lay: { price: '--', size: 0 } },
    x: { back: { price: '--', size: 0 }, lay: { price: '--', size: 0 } },
    two: { back: { price: '--', size: 0 }, lay: { price: '--', size: 0 } },
    totalMatched: m.totalMatched || 0,
  };

  // If runners have odds data, try to extract it
  if (hasOddsInRunners && m.runners) {
    const runners = m.runners;
    
    if (runners.length >= 2) {
      // Team 1 (runner 0)
      if (runners[0]?.ex?.availableToBack?.[0] || runners[0]?.ex?.availableToLay?.[0] || runners[0]?.lastPriceTraded) {
        initialOdds.one = {
          back: {
            price: runners[0].ex?.availableToBack?.[0]?.price || runners[0].lastPriceTraded || '--',
            size: runners[0].ex?.availableToBack?.[0]?.size || 0,
          },
          lay: {
            price: runners[0].ex?.availableToLay?.[0]?.price || '--',
            size: runners[0].ex?.availableToLay?.[0]?.size || 0,
          },
        };
      }
      
      // Team 2 (runner 1 or 2 depending on draw)
      const team2Index = runners.length === 3 ? 2 : 1;
      if (runners[team2Index]?.ex?.availableToBack?.[0] || runners[team2Index]?.ex?.availableToLay?.[0] || runners[team2Index]?.lastPriceTraded) {
        initialOdds.two = {
          back: {
            price: runners[team2Index].ex?.availableToBack?.[0]?.price || runners[team2Index].lastPriceTraded || '--',
            size: runners[team2Index].ex?.availableToBack?.[0]?.size || 0,
          },
          lay: {
            price: runners[team2Index].ex?.availableToLay?.[0]?.price || '--',
            size: runners[team2Index].ex?.availableToLay?.[0]?.size || 0,
          },
        };
      }
      
      // Draw (runner 1 if 3 runners)
      if (runners.length === 3 && runners[1]) {
        if (runners[1]?.ex?.availableToBack?.[0] || runners[1]?.ex?.availableToLay?.[0] || runners[1]?.lastPriceTraded) {
          initialOdds.x = {
            back: {
              price: runners[1].ex?.availableToBack?.[0]?.price || runners[1].lastPriceTraded || '--',
              size: runners[1].ex?.availableToBack?.[0]?.size || 0,
            },
            lay: {
              price: runners[1].ex?.availableToLay?.[0]?.price || '--',
              size: runners[1].ex?.availableToLay?.[0]?.size || 0,
            },
          };
        }
      }
    }
  }

  return {
    id: m.marketId,
    matchId: m.marketId,
    runners: m.runners,
    team1: m.runners[0]?.runnerName || '',
    team2: m.runners[1]?.runnerName || (m.runners[2]?.runnerName || ''),
    team3: m.runners[2]?.runnerName || '',
    status: upperTab === 'in-play' && m?.inplay ? 'In-Play' : timeStr,
    isLive: Boolean(upperTab === 'in-play' && m?.inplay),
    time: timeStr,
    date: utcDate,
    isSuspended: m.stopBet,
    eventid: m.version,
    matchTitle: m.marketName || `${m.runners[0]?.runnerName || ''} v ${m.runners[1]?.runnerName || m.runners[2]?.runnerName || ''}`,
    sportName: m.sportName || sportName || 'Unknown',
    sportId: m.sportId || null,
    odds: initialOdds,
    needsOddsFetch: !hasOddsInRunners, // Flag to indicate if we need to fetch odds
  };
};
